/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {Oktoberfest} from "../Oktoberfest";
import {OktoberfestSymbolState} from "./OktoberfestSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {TimelineLite, TweenLite, Elastic} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class OktoberfestSymbol extends PIXI.Container implements IBaseSymbol {
    public symbolWidth: number = 143;
    public symbolHeight: number = 154;
    public bottomPadding: number = 17;
    public symbolName: string;
    public stackedSymbolName: IParsedStackedSymbolName;
    public reelId: number;
    protected _animation: MultiSprites;
    protected _centerPosition: PIXI.Point;
    private _wildRotation: TimelineSprite;
    private static _symbolAnchorOffsetY: { [symName: string]: number } = {
        L1: -10,
        L2: -3,
        L3: -3,
        L4: -3,
        L5: -3,
        M1: -3,
        M2: -3,
        M3: -3,
        M4: -3,
        M5: -3,
        W:-3
    };

    constructor(symbolName: string, reelId: number, initState: OktoberfestSymbolState) {
        super();
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.initAnimation();
        this.getStateAnimation(initState, {duration: 0}, {duration: 0});
    }

    public get symbolStep(): number {
        return this.symbolHeight + this.bottomPadding;
    }

    public getStateAnimation(state: OktoberfestSymbolState, inVars: gsap.TweenVars, outVars: gsap.TweenVars, frames?: number[]): TimelineLite {
        this.createState(state);
        const tl: TimelineLite = new TimelineLite({onComplete:()=>{
            if(this.symbolName=="W3") {
                this.hideAllStates();
                this._animation.sprites[state].alpha=1;
                this._animation.sprites[state].visible=true;
            }
            }});
       tl.add(this._animation.playSprite(state, inVars, outVars, frames),0.01);
        return tl;
    }
    private hideAllStates():void{
        if(this._animation.sprites["normal"]) {
            this._animation.sprites["normal"].alpha = 0;
            this._animation.sprites["normal"].visible=false;
        }
        if(this._animation.sprites["land"]) {
            this._animation.sprites["land"].alpha = 0;
            this._animation.sprites["land"].visible=false;
        }
        if(this._animation.sprites["drop"]) {
            this._animation.sprites["drop"].alpha = 0;
            this._animation.sprites["drop"].visible=false;
        }
        if(this._animation.sprites["win"]){
            this._animation.sprites["win"].alpha=0;
            this._animation.sprites["win"].visible=false;
        }
    }

    public onRemove(): void {
        this.destroy({children: true});
    }

    public getGlobalPosition(): PIXI.Point {
        if (this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    public playLanding(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
        });
        tl.to(this, {duration: 0.01});
        return tl;
    }

    public lowSymAnimation(): TimelineLite {
        const pretzelScaleTimeLine = new TimelineLite({});
        pretzelScaleTimeLine.add(TweenLite.to(this._animation.sprites["normal"].scale, 0.1, {x: 0.7}));
        pretzelScaleTimeLine.add(TweenLite.to(this._animation.sprites["normal"].scale, 0.1, {x: 0}));
        pretzelScaleTimeLine.add(this.playFlipAnimation());
        return pretzelScaleTimeLine;
    }

    public playFlipAnimation(): TimelineLite {
        if (!this._wildRotation) {
            this._wildRotation = new TimelineSprite(Oktoberfest.assetLoader.getTextures("W_rotation"));
            this._wildRotation.anchor.set(0.5);
            this._wildRotation.y = this.getCenterPosition().y;
        }

        const wildRotationTl: TimelineLite = new TimelineLite({});
        wildRotationTl.add(()=>{
            this.updateLayers();
            this._animation.sprites["normal"].hide();
            this._wildRotation.visible = true;
        },0);
        wildRotationTl.add(this._wildRotation.getAnimation());
        return wildRotationTl;
    }

    protected updateLayers(): void {
        if (this._wildRotation) {
            this.addChild(this._wildRotation);
        }
    }

    protected initAnimation(): void {
        this._centerPosition = this.getCenterPosition();
        this._animation = new MultiSprites();
        this._animation.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(this._animation);
    }

    protected getCenterPosition(): PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    protected createState(state: OktoberfestSymbolState): void {
        if (!this._animation.sprites[state]) {
            this._animation.addSprites({
                name: state,
                textures: this.getStateTexture(this.symbolName, state),
                anchor: new PIXI.Point(0.5, 0.5)
            });
            if (state === OktoberfestSymbolState.SPIN) {
                if( this._animation.sprites["spin"]){
                    this._animation.sprites["spin"].scale.set(2);
                }
            }
            if (state === OktoberfestSymbolState.NO_WIN) {
                this._animation.sprites[state].tint = 0x555555;
            }
            if (state === OktoberfestSymbolState.DROP && this.symbolName == "W3") {
                this._animation.sprites["normal"].hide();
                this._animation.sprites[state].position.set(1,-67);
                this._animation.sprites[state].name ="drop";
            }
            if (state === OktoberfestSymbolState.WIN && this.symbolName == "W3") {
                this._animation.sprites["normal"].hide();
                this._animation.sprites[state].y = -57;
                this._animation.sprites[state].name ="win";
            }
            if (state === OktoberfestSymbolState.LAND && this.symbolName == "W3") {
                this._animation.sprites["normal"].hide();
                this._animation.sprites[state].name ="land";
                this._animation.sprites[state].position.set(-5,-4);
            }
            if(this.symbolName==="W3"){
                this._animation.sprites["normal"].position.set(-2,-6);
                this.hideAllStates();
                this._animation.sprites[state].alpha=1;
                this._animation.sprites[state].visible=true;
            }
            if (state === OktoberfestSymbolState.WIN) {
                if(this.symbolName.indexOf("L")>-1 || this.symbolName.indexOf("M")>-1){
                    this._animation.sprites[state].x = OktoberfestSymbol._symbolAnchorOffsetY[this.symbolName];
                }
                this.addChild(this._animation);
            }
        }
    }

    protected getStateTexture(symName: string, state?: OktoberfestSymbolState): PIXI.Texture[] {
        let symbolName: string = symName.split("_")[0];
        let texturePath: string = `${symbolName}/${symbolName}`;
        if (state === OktoberfestSymbolState.SPIN) {
            texturePath += `_${state}`;
        }
        if (state === OktoberfestSymbolState.WIN) {
            texturePath = symName + `_${state}`;
        }
        if ((state === OktoberfestSymbolState.DROP || state === OktoberfestSymbolState.LAND) && symName == "W3") {
            texturePath = symName + `_${state}`;
        }

        return Oktoberfest.assetLoader.getTextures(texturePath);
    }

}